import { FeatureFlags } from "~/v2/helpers/featureFlags/types";
import { UNLEASH_CLIENT } from "~/v2/helpers/featureFlags/unleash";
import { parseCryptoAssetSymbol } from "~/v2/utils/cryptoAssets";

export const isTokenizedEquitySymbol = (symbol: string): boolean =>
  symbol.endsWith(".TOKEN") && !symbol.includes("/");

/** Canonical us-1 / trade symbol from wallet or legacy quote forms. */
export const getTokenizedEquityMarketDataSymbol = (
  symbol: string,
): string | undefined => {
  if (symbol.includes("/")) {
    const { asset } = parseCryptoAssetSymbol(symbol);
    if (isTokenizedEquitySymbol(asset)) {
      return asset;
    }

    const [base] = symbol.split("/");
    if (isTokenizedEquitySymbol(base)) {
      return base;
    }

    return undefined;
  }

  if (isTokenizedEquitySymbol(symbol)) {
    return symbol;
  }

  return undefined;
};

export const isTokenizedEquityMarketDataSymbol = (symbol: string): boolean =>
  getTokenizedEquityMarketDataSymbol(symbol) !== undefined;

export const isTokenizedEquityWalletSymbol = (symbol: string): boolean =>
  isTokenizedEquityMarketDataSymbol(symbol);

export const isTokenizedEquityTradingEnabled = (): boolean =>
  UNLEASH_CLIENT?.isEnabled(FeatureFlags.TOKENIZED_EQUITY_TRADING) ?? false;

export const shouldUseTokenizedEquityMarketData = (symbol: string): boolean =>
  isTokenizedEquityTradingEnabled() &&
  isTokenizedEquityMarketDataSymbol(symbol) &&
  !!window.env.TOKENIZED_EQUITY_DATA_API_URL;

export const shouldUseTokenizedEquityWallet = (
  symbol: string | undefined,
  isTokenizedEquityTradingEnabled: boolean,
): boolean =>
  isTokenizedEquityTradingEnabled &&
  !!symbol &&
  isTokenizedEquityWalletSymbol(symbol);

export const shouldUseTokenizedEquityTimeInForce = (
  symbol: string,
  isTokenizedEquityTradingEnabled: boolean,
): boolean =>
  isTokenizedEquityTradingEnabled && isTokenizedEquitySymbol(symbol);

export const getTokenizedEquityDataApiUrl = (): string | undefined =>
  window.env.TOKENIZED_EQUITY_DATA_API_URL;

/** Securities wallet base; explicit env preferred, derivation until wallet refactor lands. */
export const getTokenizedEquityWalletApiUrl = (): string => {
  const configured = window.env.TOKENIZED_EQUITY_WALLET_API_URL;
  if (configured) {
    return configured;
  }

  const url = window.env.CRYPTO_WALLET_API_URL;
  if (url.endsWith("/ts")) {
    return url;
  }
  if (url.endsWith("/v1")) {
    return `${url.slice(0, -2)}ts`;
  }
  if (url.endsWith("/wallet-api")) {
    return `${url}/ts`;
  }
  return url.replace(/\/v\d+$/, "/ts");
};
